import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Business,
  Save,
  ArrowBack
} from '@mui/icons-material';
import api from '../services/api';
import LocationInput from '../components/common/LocationInput';

// Keep in sync with the filter options in MerchantDirectory.js — these values
// drive discoverability in the public supplier directory.
const SPECIALIZATION_OPTIONS = [
  'Cement & Concrete',
  'Bricks & Blocks',
  'Steel & Rebar',
  'Roofing Materials',
  'Timber',
  'Plumbing',
  'Electrical',
  'Paint & Finishes'
];

const SERVICE_AREA_OPTIONS = [
  'Harare', 'Bulawayo', 'Chitungwiza', 'Mutare', 'Gweru', 'Norton',
  'Masvingo', 'Kwekwe', 'Kadoma', 'Marondera'
];

const MerchantProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    businessName: '',
    tradingName: '',
    businessType: 'Company',
    registrationNumber: '',
    tinNumber: '',
    vatNumber: '',
    businessEmail: user?.email || '',
    businessPhone: '',
    physicalAddress: {
      street: '',
      suburb: '',
      city: '',
      province: 'Harare',
      postal_code: '',
      gps_coordinates: { lat: null, lng: null }
    },
    ownerName: '',
    ownerIdNumber: '',
    ownerPhone: '',
    specializations: [],
    serviceAreas: [],
    minimumOrderValue: 0,
    deliveryOptions: {
      offers_delivery: false,
      delivery_fee_structure: '',
      delivery_radius_km: 0,
      delivery_time_estimate: ''
    },
    bankDetails: {
      bank_name: '',
      account_name: '',
      account_number: '',
      branch: '',
      swift_code: ''
    },
    mobileMoney: {
      provider: 'EcoCash',
      number: ''
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/merchants/profile/me');

      if (response.data.success) {
        setProfile(response.data.data);
        // Populate form with existing data
        const data = response.data.data;
        setFormData({
          businessName: data.business_name || '',
          tradingName: data.trading_name || '',
          businessType: data.business_type || 'Company',
          registrationNumber: data.registration_number || '',
          tinNumber: data.tin_number || '',
          vatNumber: data.vat_number || '',
          businessEmail: data.business_email || user?.email || '',
          businessPhone: data.business_phone || '',
          physicalAddress: data.physical_address || formData.physicalAddress,
          ownerName: data.owner_name || '',
          ownerIdNumber: data.owner_id_number || '',
          ownerPhone: data.owner_phone || '',
          specializations: data.specializations || [],
          serviceAreas: data.service_areas || [],
          minimumOrderValue: parseFloat(data.minimum_order_value) || 0,
          deliveryOptions: data.delivery_options || formData.deliveryOptions,
          bankDetails: data.bank_details || formData.bankDetails,
          mobileMoney: data.mobile_money || formData.mobileMoney
        });
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to load merchant profile');
        console.error('Error fetching profile:', err);
      }
      // 404 is okay - means profile doesn't exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        physicalAddress: {
          ...prev.physicalAddress,
          [field]: value
        }
      }));
    } else if (name.startsWith('bank.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [field]: value
        }
      }));
    } else if (name.startsWith('mobile.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        mobileMoney: {
          ...prev.mobileMoney,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = profile ? '/merchants/profile/me' : '/merchants/profile';
      const method = profile ? 'put' : 'post';

      const response = await api[method](endpoint, formData);

      if (response.data.success) {
        setSuccess(profile ? 'Profile updated successfully!' : 'Profile created successfully!');
        setProfile(response.data.data);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save profile');
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" fontWeight={700}>
          {profile ? 'Edit Merchant Profile' : 'Create Merchant Profile'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Business Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name *"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Trading Name (DBA)"
                name="tradingName"
                value={formData.tradingName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Business Type *</InputLabel>
                <Select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  label="Business Type *"
                >
                  <MenuItem value="Sole Proprietor">Sole Proprietor</MenuItem>
                  <MenuItem value="Partnership">Partnership</MenuItem>
                  <MenuItem value="Company">Company</MenuItem>
                  <MenuItem value="Cooperative">Cooperative</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Registration Number *"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TIN Number *"
                name="tinNumber"
                value={formData.tinNumber}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VAT Number"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Contact Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Email *"
                name="businessEmail"
                type="email"
                value={formData.businessEmail}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Phone *"
                name="businessPhone"
                value={formData.businessPhone}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Physical Address
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <LocationInput
                label="Physical Address"
                value={formData.physicalAddress && Object.keys(formData.physicalAddress).length > 0 
                  ? {
                      address: formData.physicalAddress.street 
                        ? `${formData.physicalAddress.street}, ${formData.physicalAddress.suburb || ''}, ${formData.physicalAddress.city || ''}`.replace(/^,\s*|,\s*$/g, '')
                        : formData.physicalAddress.address || '',
                      street: formData.physicalAddress.street || '',
                      suburb: formData.physicalAddress.suburb || '',
                      city: formData.physicalAddress.city || '',
                      province: formData.physicalAddress.province || '',
                      postalCode: formData.physicalAddress.postal_code || '',
                      country: formData.physicalAddress.country || '',
                      coordinates: formData.physicalAddress.gps_coordinates || null
                    }
                  : null}
                onChange={(location) => {
                  if (location) {
                    setFormData(prev => ({
                      ...prev,
                      physicalAddress: {
                        street: location.street || location.addressComponents?.route || '',
                        suburb: location.suburb || location.addressComponents?.sublocality || '',
                        city: location.city || location.addressComponents?.locality || '',
                        province: location.province || location.addressComponents?.administrativeAreaLevel1 || '',
                        postal_code: location.postalCode || location.addressComponents?.postalCode || '',
                        country: location.country || location.addressComponents?.country || '',
                        gps_coordinates: location.coordinates || null,
                        address: location.address || ''
                      }
                    }));
                  }
                }}
                placeholder="Enter business address..."
                required
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Owner/Representative Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Owner Name *"
                name="ownerName"
                value={formData.ownerName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Owner ID Number *"
                name="ownerIdNumber"
                value={formData.ownerIdNumber}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Owner Phone *"
                name="ownerPhone"
                value={formData.ownerPhone}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Products & Delivery
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                freeSolo
                options={SPECIALIZATION_OPTIONS}
                value={formData.specializations}
                onChange={(e, value) => setFormData(prev => ({ ...prev, specializations: value }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material Categories You Supply"
                    helperText="Buyers filter the supplier directory by these"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                freeSolo
                options={SERVICE_AREA_OPTIONS}
                value={formData.serviceAreas}
                onChange={(e, value) => setFormData(prev => ({ ...prev, serviceAreas: value }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Areas"
                    helperText="Cities/areas you supply or deliver to"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Order Value (USD)"
                name="minimumOrderValue"
                value={formData.minimumOrderValue}
                onChange={(e) => setFormData(prev => ({ ...prev, minimumOrderValue: e.target.value === '' ? 0 : parseFloat(e.target.value) }))}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.deliveryOptions.offers_delivery)}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryOptions: { ...prev.deliveryOptions, offers_delivery: e.target.checked }
                    }))}
                  />
                }
                label="We offer delivery"
              />
            </Grid>
            {formData.deliveryOptions.offers_delivery && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Delivery Radius (km)"
                    value={formData.deliveryOptions.delivery_radius_km || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryOptions: { ...prev.deliveryOptions, delivery_radius_km: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }
                    }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Typical Delivery Time"
                    placeholder="e.g. Same day within Harare"
                    value={formData.deliveryOptions.delivery_time_estimate || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryOptions: { ...prev.deliveryOptions, delivery_time_estimate: e.target.value }
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Delivery Fee Structure"
                    placeholder="e.g. $20 flat within 20km"
                    value={formData.deliveryOptions.delivery_fee_structure || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryOptions: { ...prev.deliveryOptions, delivery_fee_structure: e.target.value }
                    }))}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Bank Details
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name *"
                name="bank.bank_name"
                value={formData.bankDetails.bank_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Name *"
                name="bank.account_name"
                value={formData.bankDetails.account_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number *"
                name="bank.account_number"
                value={formData.bankDetails.account_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Branch *"
                name="bank.branch"
                value={formData.bankDetails.branch}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SWIFT Code"
                name="bank.swift_code"
                value={formData.bankDetails.swift_code}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Mobile Money
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  name="mobile.provider"
                  value={formData.mobileMoney.provider || 'EcoCash'}
                  label="Provider"
                  onChange={handleChange}
                >
                  <MenuItem value="EcoCash">EcoCash</MenuItem>
                  <MenuItem value="OneMoney">OneMoney</MenuItem>
                  <MenuItem value="InnBucks">InnBucks</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Money Number"
                name="mobile.number"
                value={formData.mobileMoney.number || ''}
                onChange={handleChange}
                placeholder="+263 7xx xxx xxx"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={saving}
              sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
            >
              {saving ? <CircularProgress size={24} /> : (profile ? 'Update Profile' : 'Create Profile')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default MerchantProfilePage;

